import React, { useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface DroppedDocument {
  id: string;
  name: string;
  size: number;
  type: string;
  progress: number;
  status: 'uploading' | 'processing' | 'ocr_processing' | 'completed' | 'error';
  errorMessage?: string;
  uploadedAt: Date;
  file?: File;
  isEmail?: boolean;
  documentType?: string; // Document type (Quote, Binder, Policy, Loss Run, Other)
  processingMetadata?: {
    extractionMethod?: 'pdf_only' | 'ocr_only' | 'hybrid' | 'pdf_fallback';
    extractionQuality?: 'high' | 'medium' | 'low' | 'failed';
    confidence?: number;
    ocrAttempted?: boolean;
    processingTime?: number;
  };
}

interface DocumentDropZoneProps {
  onDocumentsAdded: (documents: DroppedDocument[]) => void;
  /**
   * Called when the user removes a file that had already finished uploading
   * (and therefore already reached the parent via onDocumentsAdded).
   */
  onDocumentRemoved?: (documentId: string) => void;
  className?: string;
  disabled?: boolean;
  customerId?: string;
  projectId?: string;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  hideCompletionIcons?: boolean; // Hide individual completion checkmarks in workflow contexts
}

const DocumentDropZone: React.FC<DocumentDropZoneProps> = ({
  onDocumentsAdded,
  onDocumentRemoved,
  className = '',
  disabled = false,
  customerId,
  projectId,
  maxFiles = 5,
  maxFileSize = 50,
  hideCompletionIcons = false
}) => {
  const { token, isLoading: authLoading } = useAuth();
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<DroppedDocument[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [renameDialog, setRenameDialog] = useState<{
    isOpen: boolean;
    files: File[];
    currentIndex: number;
    renamedFiles: Array<{file: File; newName: string; documentType: string}>;
  }>({
    isOpen: false,
    files: [],
    currentIndex: 0,
    renamedFiles: []
  });
  const [currentFileName, setCurrentFileName] = useState('');
  const [currentDocumentType, setCurrentDocumentType] = useState('Other');

  const generateFileId = () => `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Helper to extract file extension
  const getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    return lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '';
  };

  // Helper to get filename without extension
  const getFilenameWithoutExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    return lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;
  };

  // Accepted file types for document analysis
  const acceptedTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'message/rfc822',
    'application/vnd.ms-outlook',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-excel', // .xls
    'text/csv', // .csv
    'application/csv' // .csv (alternative MIME type)
  ];

  // File validation
  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxFileSize * 1024 * 1024) {
      return `File size exceeds ${maxFileSize}MB limit`;
    }

    // Check file type
    const isValidType = acceptedTypes.includes(file.type) ||
                       file.name.toLowerCase().endsWith('.pdf') ||
                       file.name.toLowerCase().endsWith('.doc') ||
                       file.name.toLowerCase().endsWith('.docx') ||
                       file.name.toLowerCase().endsWith('.eml') ||
                       file.name.toLowerCase().endsWith('.msg') ||
                       file.name.toLowerCase().endsWith('.xlsx') ||
                       file.name.toLowerCase().endsWith('.xls') ||
                       file.name.toLowerCase().endsWith('.csv');

    if (!isValidType) {
      return 'Unsupported file type. Please upload PDF, DOC, DOCX, Excel (XLSX/XLS), CSV, EML, or MSG files.';
    }

    return null;
  };

  // Upload file to backend
  const uploadFileToBackend = async (fileData: DroppedDocument): Promise<DroppedDocument> => {
    try {
      // Check if it's an email file
      const isEmailFile = fileData.type === 'message/rfc822' || 
                         fileData.type === 'application/vnd.ms-outlook' ||
                         fileData.name.toLowerCase().endsWith('.eml') ||
                         fileData.name.toLowerCase().endsWith('.msg');

      // Determine the upload endpoint
      const uploadEndpoint = isEmailFile 
        ? `${process.env.REACT_APP_API_BASE_URL || '/api'}/documents/email/upload`
        : `${process.env.REACT_APP_API_BASE_URL || '/api'}/documents`;

      // Create form data
      const formData = new FormData();
      if (fileData.file) {
        formData.append('file', fileData.file);
        if (customerId) {
          formData.append('customerId', customerId);
        }
        if (projectId) {
          formData.append('projectId', projectId);
        }
        if (fileData.documentType) {
          formData.append('policyType', fileData.documentType);
        }
      }

      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // Upload timeout based on file type
      const mightRequireOCR = fileData.file?.name.toLowerCase().endsWith('.pdf');
      let uploadTimeout = 30000; // 30s for regular files
      if (isEmailFile) {
        uploadTimeout = 120000; // 2 minutes for emails
      } else if (mightRequireOCR) {
        uploadTimeout = 1200000; // 20 minutes for OCR-requiring files
      }
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), uploadTimeout);

      try {
        const response = await fetch(uploadEndpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Upload failed', code: 'UNKNOWN_ERROR' }));
          
          // Handle specific backend error codes
          let userFriendlyMessage = errorData.error || `Upload failed with status ${response.status}`;
          
          if (errorData.code === 'MEMORY_ERROR') {
            userFriendlyMessage = 'Server is experiencing high memory usage. Please try uploading a smaller file or wait a moment and try again.';
          } else if (errorData.code === 'TIMEOUT_ERROR') {
            userFriendlyMessage = 'Document processing timed out. Please try a smaller file or simpler document.';
          } else if (errorData.code === 'FILE_TOO_LARGE') {
            userFriendlyMessage = errorData.error;
          }
          
          console.error(`Upload failed for ${fileData.name}:`, {
            status: response.status,
            error: errorData.error,
            code: errorData.code,
            timestamp: errorData.timestamp
          });
          
          throw new Error(userFriendlyMessage);
        }
        
        const data = await response.json();
        
        // For email uploads, return the parent document ID, for regular uploads return document ID
        const documentId = isEmailFile ? data.email?.parentDocumentId : data.id;
        
        if (!documentId) {
          throw new Error('No document ID received from backend');
        }
        
        return {
          ...fileData,
          id: documentId,
          progress: 100,
          status: 'completed'
        };
      } catch (error) {
        clearTimeout(timeoutId);
        
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error(`Upload timeout - file took longer than ${uploadTimeout/1000} seconds to upload`);
        }
        throw error;
      }
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  // Process files
  const processFiles = useCallback(async (files: FileList | File[]) => {
    if (disabled) return;
    
    if (authLoading) {
      alert('Authentication is still loading. Please wait a moment and try again.');
      return;
    }
    
    if (!token) {
      alert('No authentication token found. Please log in again.');
      return;
    }

    const fileArray = Array.from(files);

    // Validate file count. Only files still in flight count toward the cap —
    // completed/errored entries stay in the list for display, and counting
    // them would make the zone dead after `maxFiles` lifetime uploads.
    const inFlight = uploadingFiles.filter(f =>
      f.status === 'uploading' || f.status === 'processing' || f.status === 'ocr_processing'
    ).length;
    if (inFlight + fileArray.length > maxFiles) {
      alert(`Cannot upload more than ${maxFiles} files at once`);
      return;
    }

    // Open rename dialog for all files
    setRenameDialog({
      isOpen: true,
      files: fileArray,
      currentIndex: 0,
      renamedFiles: []
    });
    // Set initial filename without extension
    setCurrentFileName(getFilenameWithoutExtension(fileArray[0]?.name || ''));
  }, [disabled, authLoading, token, uploadingFiles.length, maxFiles]);

  // Handle rename dialog confirmation
  const handleRenameConfirm = useCallback(() => {
    const currentFile = renameDialog.files[renameDialog.currentIndex];
    if (!currentFile) return;

    // Get the original file extension
    const originalExtension = getFileExtension(currentFile.name);

    // Get the user's input (trimmed)
    let userInput = currentFileName.trim() || getFilenameWithoutExtension(currentFile.name);

    // Check if user already added the extension (prevent .pdf.pdf)
    if (userInput.toLowerCase().endsWith(originalExtension.toLowerCase())) {
      // User already included the extension, use as-is
      // But ensure we don't have double extensions by removing and re-adding
      userInput = userInput.substring(0, userInput.length - originalExtension.length);
    }

    // Append the original extension
    const newName = userInput + originalExtension;

    const updatedRenamedFiles = [...renameDialog.renamedFiles, {
      file: currentFile,
      newName,
      documentType: currentDocumentType
    }];

    if (renameDialog.currentIndex < renameDialog.files.length - 1) {
      // Move to next file
      const nextIndex = renameDialog.currentIndex + 1;
      setRenameDialog({
        ...renameDialog,
        currentIndex: nextIndex,
        renamedFiles: updatedRenamedFiles
      });
      // Set filename without extension for next file
      setCurrentFileName(getFilenameWithoutExtension(renameDialog.files[nextIndex].name));
      setCurrentDocumentType('Other'); // Reset to default for next file
    } else {
      // All files renamed, proceed with upload
      setRenameDialog({ isOpen: false, files: [], currentIndex: 0, renamedFiles: [] });
      processRenamedFiles(updatedRenamedFiles);
    }
  }, [renameDialog, currentFileName, currentDocumentType]);

  // Process renamed files
  const processRenamedFiles = useCallback(async (renamedFiles: Array<{file: File; newName: string; documentType: string}>) => {
    // Create initial file objects with renamed names
    const newFiles: DroppedDocument[] = renamedFiles.map(({ file, newName, documentType }) => {
      const validationError = validateFile(file);
      // Create a new File object with the renamed name
      const renamedFile = new File([file], newName, { type: file.type });
      
      return {
        id: generateFileId(),
        name: newName,
        size: file.size,
        type: file.type,
        progress: 0,
        status: validationError ? 'error' : 'uploading',
        errorMessage: validationError || undefined,
        uploadedAt: new Date(),
        file: renamedFile,
        documentType: documentType,
        isEmail: file.type === 'message/rfc822' || 
                file.type === 'application/vnd.ms-outlook' ||
                file.name.toLowerCase().endsWith('.eml') ||
                file.name.toLowerCase().endsWith('.msg')
      };
    });

    setUploadingFiles(prev => [...prev, ...newFiles]);

    // Upload files
    const uploadPromises = newFiles
      .filter(file => !file.errorMessage)
      .map(async (file) => {
        try {
          // Update progress
          setUploadingFiles(prev => 
            prev.map(f => f.id === file.id ? { ...f, progress: 50 } : f)
          );

          const uploadedFile = await uploadFileToBackend(file);
          
          // Update status
          setUploadingFiles(prev => 
            prev.map(f => f.id === file.id ? uploadedFile : f)
          );

          return uploadedFile;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Upload failed';
          
          setUploadingFiles(prev => 
            prev.map(f => 
              f.id === file.id 
                ? { ...f, status: 'error', errorMessage, progress: 0 }
                : f
            )
          );
          
          return { ...file, status: 'error' as const, errorMessage };
        }
      });

    // Wait for all uploads and notify parent
    const results = await Promise.all(uploadPromises);
    const successfulUploads = results.filter(file => file.status === 'completed');
    
    if (successfulUploads.length > 0) {
      onDocumentsAdded(successfulUploads);
    }
  }, [onDocumentsAdded, customerId, projectId, token, uploadFileToBackend]);
  
  // Handle skip current file in rename dialog
  const handleRenameSkip = useCallback(() => {
    const currentFile = renameDialog.files[renameDialog.currentIndex];
    if (!currentFile) return;

    // Use original filename with current document type
    const updatedRenamedFiles = [...renameDialog.renamedFiles, {
      file: currentFile,
      newName: currentFile.name,
      documentType: currentDocumentType
    }];

    if (renameDialog.currentIndex < renameDialog.files.length - 1) {
      // Move to next file
      const nextIndex = renameDialog.currentIndex + 1;
      setRenameDialog({
        ...renameDialog,
        currentIndex: nextIndex,
        renamedFiles: updatedRenamedFiles
      });
      // Set filename without extension for next file
      setCurrentFileName(getFilenameWithoutExtension(renameDialog.files[nextIndex].name));
      setCurrentDocumentType('Other'); // Reset to default for next file
    } else {
      // All files processed, proceed with upload
      setRenameDialog({ isOpen: false, files: [], currentIndex: 0, renamedFiles: [] });
      processRenamedFiles(updatedRenamedFiles);
    }
  }, [renameDialog, processRenamedFiles, currentDocumentType]);

  // Handle cancel rename dialog
  const handleRenameCancel = useCallback(() => {
    setRenameDialog({ isOpen: false, files: [], currentIndex: 0, renamedFiles: [] });
    setCurrentFileName('');
  }, []);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  }, [disabled, processFiles]);

  // File input handler
  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    
    // Reset input value
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [processFiles]);

  // Clear completed files
  const clearCompleted = () => {
    setUploadingFiles(prev => prev.filter(file => file.status === 'uploading' || file.status === 'processing' || file.status === 'ocr_processing'));
  };

  // Remove specific file. For a completed upload the parent already has the
  // document in its context, so it needs to be told to drop it too.
  const removeFile = (fileId: string) => {
    const removed = uploadingFiles.find(file => file.id === fileId);
    setUploadingFiles(prev => prev.filter(file => file.id !== fileId));
    if (removed?.status === 'completed') {
      onDocumentRemoved?.(fileId);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          isDragOver
            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.eml,.msg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/csv,message/rfc822,application/vnd.ms-outlook"
          onChange={handleFileInput}
          className="hidden"
          disabled={disabled}
        />
        
        <div className="flex flex-col items-center gap-2">
          <svg
            className="w-8 h-8 text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Drop additional documents here</span>
            <br />
            or click to browse
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-500">
            PDF, DOC, DOCX, Excel, CSV, EML, MSG files up to {maxFileSize}MB
          </div>
        </div>
      </div>

      {/* Uploading Files List */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Additional Documents ({uploadingFiles.length})
            </h4>
            {uploadingFiles.some(f => f.status === 'completed' || f.status === 'error') && (
              <button
                onClick={clearCompleted}
                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Clear completed
              </button>
            )}
          </div>
          
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {uploadingFiles.map((file) => (
              <div key={file.id} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {file.name}
                    {file.isEmail && <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">📧</span>}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(file.size)}
                  </div>
                  
                  {/* Progress bar */}
                  {file.status === 'uploading' && (
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                  )}
                  
                  {/* Error message */}
                  {file.status === 'error' && file.errorMessage && (
                    <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {file.errorMessage}
                    </div>
                  )}
                </div>
                
                {/* Status icon */}
                <div className="flex-shrink-0">
                  {file.status === 'uploading' && (
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  )}
                  {file.status === 'processing' && (
                    <div className="w-4 h-4 text-yellow-500">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2L3 7v11a1 1 0 001 1h3v-5a1 1 0 011-1h4a1 1 0 011 1v5h3a1 1 0 001-1V7l-7-5z"/>
                      </svg>
                    </div>
                  )}
                  {file.status === 'ocr_processing' && (
                    <div className="w-4 h-4 text-orange-500">
                      <svg fill="currentColor" viewBox="0 0 20 20" className="animate-pulse">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                  )}
                  {file.status === 'completed' && !hideCompletionIcons && (
                    <div className="w-4 h-4 text-green-500">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                  )}
                  {file.status === 'error' && (
                    <div className="w-4 h-4 text-red-500">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                    </div>
                  )}
                </div>
                
                {/* Remove button */}
                <button
                  onClick={() => removeFile(file.id)}
                  className="flex-shrink-0 w-4 h-4 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Rename Dialog */}
      {renameDialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Rename Document {renameDialog.currentIndex + 1} of {renameDialog.files.length}
            </h3>
            
            {/* Current file info */}
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Original name:</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white break-all">
                {renameDialog.files[renameDialog.currentIndex]?.name}
              </p>
              {renameDialog.files[renameDialog.currentIndex]?.name.toLowerCase().endsWith('.eml') ||
               renameDialog.files[renameDialog.currentIndex]?.name.toLowerCase().endsWith('.msg') ? (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  📧 Email file - attachments will be extracted automatically
                </p>
              ) : null}
            </div>
            
            {/* Rename input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                New name (extension will be added automatically):
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={currentFileName}
                  onChange={(e) => setCurrentFileName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleRenameConfirm();
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  placeholder="Enter new filename..."
                  autoFocus
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400 pointer-events-none">
                  {getFileExtension(renameDialog.files[renameDialog.currentIndex]?.name || '')}
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Final name: {currentFileName.trim() || getFilenameWithoutExtension(renameDialog.files[renameDialog.currentIndex]?.name || '')}{getFileExtension(renameDialog.files[renameDialog.currentIndex]?.name || '')}
              </p>
            </div>
            
            {/* Document Type Dropdown */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Document Type:
              </label>
              <select
                value={currentDocumentType}
                onChange={(e) => setCurrentDocumentType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              >
                <option value="Quote">Quote</option>
                <option value="Binder">Binder</option>
                <option value="Policy">Policy</option>
                <option value="Loss Run">Loss Run</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            {/* Actions */}
            <div className="flex justify-between gap-3">
              <button
                onClick={handleRenameCancel}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                Cancel All
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleRenameSkip}
                  className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Skip
                </button>
                <button
                  onClick={handleRenameConfirm}
                  className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  {renameDialog.currentIndex < renameDialog.files.length - 1 ? 'Next' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentDropZone;